import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const COUNTRIES = [
  "United States", "United Kingdom", "Germany", "France", "Israel", "United Arab Emirates",
  "Netherlands", "Spain", "Italy", "Switzerland", "Canada", "Australia", "India",
  "Belgium", "Austria", "Sweden", "Norway", "Denmark", "Finland", "Ireland",
  "Portugal", "Poland", "Czech Republic", "Romania", "Hungary", "Greece",
  "Turkey", "South Africa", "Brazil", "Mexico", "Japan", "South Korea",
  "Singapore", "Hong Kong", "China", "Russia", "Saudi Arabia", "Qatar",
  "Kuwait", "Bahrain", "Oman", "Jordan", "Egypt", "Nigeria", "Kenya",
  "New Zealand", "Philippines", "Thailand", "Vietnam", "Indonesia", "Malaysia"
];

export default function PubBankDetails() {
  const [form, setForm] = useState({
    beneficiary_name: "", company_name: "", beneficiary_address: "",
    beneficiary_city: "", beneficiary_state: "", beneficiary_zip: "",
    beneficiary_country: "", bank_name: "", bank_address: "",
    account_number: "", iban: "", swift_bic: "", aba_routing: "", account_type: "Checking"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/publisher/bank-details");
        if (data && Object.keys(data).length > 0) {
          setForm((prev) => ({ ...prev, ...data }));
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.post("/publisher/bank-details", form);
      toast.success("Bank details saved successfully");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading bank details...</div>;

  return (
    <div className="p-8" data-testid="publisher-bank-details-page">
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
          Payouts / Wire Transfer
        </p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">Bank Details</h1>
        <p className="text-sm text-slate-500 mt-2">Wire transfer details for payouts</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        {/* Beneficiary Section */}
        <div className="border border-slate-200 rounded-md p-5">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Beneficiary Information</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Beneficiary Name *</Label>
                <Input name="beneficiary_name" value={form.beneficiary_name} onChange={handleChange} required className="h-10" data-testid="bank-beneficiary-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Company Name</Label>
                <Input name="company_name" value={form.company_name} onChange={handleChange} className="h-10" data-testid="bank-company-name" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Address *</Label>
              <Input name="beneficiary_address" value={form.beneficiary_address} onChange={handleChange} required className="h-10" data-testid="bank-address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">City *</Label>
                <Input name="beneficiary_city" value={form.beneficiary_city} onChange={handleChange} required className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">State/Province</Label>
                <Input name="beneficiary_state" value={form.beneficiary_state} onChange={handleChange} className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Zip/Postal *</Label>
                <Input name="beneficiary_zip" value={form.beneficiary_zip} onChange={handleChange} required className="h-10" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Country *</Label>
              <Select value={form.beneficiary_country} onValueChange={(v) => setForm({ ...form, beneficiary_country: v })}>
                <SelectTrigger className="h-10" data-testid="bank-country-select">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Bank Section */}
        <div className="border border-slate-200 rounded-md p-5">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Bank Information</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Bank Name *</Label>
                <Input name="bank_name" value={form.bank_name} onChange={handleChange} required className="h-10" data-testid="bank-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Account Type</Label>
                <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                  <SelectTrigger className="h-10" data-testid="bank-account-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Checking">Checking</SelectItem>
                    <SelectItem value="Savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Bank Address</Label>
              <Input name="bank_address" value={form.bank_address} onChange={handleChange} className="h-10" data-testid="bank-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Account Number / IBAN *</Label>
                <Input name="account_number" value={form.account_number} onChange={handleChange} required className="h-10 font-mono" data-testid="bank-account-number" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">IBAN (if different)</Label>
                <Input name="iban" value={form.iban} onChange={handleChange} className="h-10 font-mono" data-testid="bank-iban" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">SWIFT / BIC Code</Label>
                <Input name="swift_bic" value={form.swift_bic} onChange={handleChange} className="h-10 font-mono" data-testid="bank-swift" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">ABA Routing (US only)</Label>
                <Input name="aba_routing" value={form.aba_routing} onChange={handleChange} className="h-10 font-mono" data-testid="bank-aba" />
              </div>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          disabled={saving}
          className="bg-[#002FA7] hover:bg-[#00227A] text-white h-11 px-8"
          data-testid="save-bank-details-btn"
        >
          {saving ? "Saving..." : "Save Bank Details"}
        </Button>
      </form>
    </div>
  );
}
